import { Link } from 'react-router-dom';

interface MarketData {
  windCertOfficialPrice: string;
  solarCertOfficialPrice: string;
  hydroCertOfficialPrice: string;
  updateDate: string;
  trends: { title: string; date: string }[];
}

interface MarketTrendsProps {
  marketData: MarketData;
}

export default function MarketTrends({ marketData }: MarketTrendsProps) {
  return (
    <section className="relative w-full py-20 md:py-28 overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="/images/market-bg.jpg"
          alt="市场行情背景"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-foreground-900/75"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Left */}
          <div>
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-background-50 leading-tight">
              市场行情
              <br />
              监测
            </h2>
            <Link
              to="/market"
              className="group mt-8 inline-flex items-center gap-3 px-7 py-3 bg-background-50 text-foreground-900 text-sm font-semibold rounded-md hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
            >
              官方价格查询
              <span className="w-6 h-6 rounded-full bg-foreground-900 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <i className="ri-arrow-right-line text-background-50 text-xs"></i>
              </span>
            </Link>
          </div>

          {/* Right */}
          <div>
            <p className="text-background-100/80 text-base md:text-lg leading-relaxed mb-8">
              仅展示官方公开价格，不自行定价。
              <br />
              实时追踪绿证行业动态与政策。
            </p>

            {/* Price cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="bg-background-50/8 backdrop-blur-sm rounded-lg p-4 border border-background-50/10">
                <p className="text-background-100/60 text-xs font-label">风电绿证</p>
                <p className="text-background-50 text-xl font-bold mt-1 font-heading">
                  &yen;{marketData.windCertOfficialPrice}
                </p>
                <p className="text-background-100/40 text-xs mt-1">元/个（官方参考）</p>
              </div>
              <div className="bg-background-50/8 backdrop-blur-sm rounded-lg p-4 border border-background-50/10">
                <p className="text-background-100/60 text-xs font-label">光伏绿证</p>
                <p className="text-background-50 text-xl font-bold mt-1 font-heading">
                  &yen;{marketData.solarCertOfficialPrice}
                </p>
                <p className="text-background-100/40 text-xs mt-1">元/个（官方参考）</p>
              </div>
              <div className="bg-background-50/8 backdrop-blur-sm rounded-lg p-4 border border-background-50/10">
                <p className="text-background-100/60 text-xs font-label">水电绿证</p>
                <p className="text-background-50 text-xl font-bold mt-1 font-heading">
                  &yen;{marketData.hydroCertOfficialPrice}
                </p>
                <p className="text-background-100/40 text-xs mt-1">元/个（官方参考）</p>
              </div>
            </div>

            {/* Trends */}
            <div className="space-y-3">
              <p className="text-background-100/60 text-xs font-label mb-3">
                行业动态 · 更新于 {marketData.updateDate}
              </p>
              {marketData.trends.map((trend, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 group cursor-pointer"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-background-100/90 text-sm group-hover:text-background-50 transition-colors">
                      {trend.title}
                    </p>
                    <p className="text-background-100/30 text-xs mt-0.5">
                      {trend.date}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}